// Finds same-day handovers within one vehicle's bookings.
//
// Bars cover whole day cells, so when one rental returns on the day the next
// collects, both want the same column. Stacking them into two lanes would say
// "two bookings competing for this day" — but nothing is competing: the van
// comes back in the morning and goes out in the afternoon. So instead the
// shared day is cut corner to corner, each bar taking one triangle, and both
// still read as full-height across every day they occupy.
//
// A genuine double-booking is a different thing and must still stack, so a
// pair only counts as a handover when the timestamps do NOT overlap. That is
// why this compares times and not just dates.
//
// Pure: no DOM, no dates constructed. bar_geometry stays a function of a
// single event; knowing about neighbours lives here instead.

function dayOf(isoString) {
  return String(isoString).split("T")[0];
}

/**
 * @param {Array<{id: string|number, start: string, end: string}>} events
 *   bookings for ONE resource — the caller groups by vehicle first.
 * @returns {Map<string|number, {diagonalStart: boolean, diagonalEnd: boolean,
 *   tilesWithNext: string|number|null}>}
 *   `tilesWithNext` names the booking this one hands over TO. It is what lets
 *   lane packing use the same notion of "may share a lane" that the geometry
 *   uses to shorten the outgoing box — two independent predicates would drift,
 *   and did.
 */
export function markTurnarounds(events) {
  const flags = new Map();
  events.forEach((event) => {
    flags.set(event.id, { diagonalStart: false, diagonalEnd: false, tilesWithNext: null });
  });

  const sorted = [...events].sort((a, b) => String(a.start).localeCompare(String(b.start)));

  for (let i = 0; i < sorted.length - 1; i += 1) {
    const outgoing = sorted[i];
    const incoming = sorted[i + 1];

    const sharesADay = dayOf(outgoing.end) === dayOf(incoming.start);
    // Lexicographic on ISO strings sorts chronologically, and avoids building
    // Date objects — which would drag timezone behaviour into a pure function.
    const doesNotOverlap = String(outgoing.end) <= String(incoming.start);

    if (sharesADay && doesNotOverlap) {
      flags.get(outgoing.id).diagonalEnd = true;
      flags.get(outgoing.id).tilesWithNext = incoming.id;
      flags.get(incoming.id).diagonalStart = true;
    }
  }

  return flags;
}

/**
 * markTurnarounds for a mixed list, grouped by resource first.
 *
 * A handover only exists WITHIN one vehicle's bookings: pairing across vehicles
 * would read two unrelated rentals as a handover and shorten the wrong bar. The
 * week view avoids this for free by painting one resource at a time, but the
 * month grid mixes every vehicle into a single row — so it groups here.
 *
 * @param {Array<{id: string|number, resourceId: string|number, start: string, end: string}>} events
 * @returns {Map<string|number, {diagonalStart: boolean, diagonalEnd: boolean,
 *   tilesWithNext: string|number|null}>} one flat map keyed by booking id
 */
export function markTurnaroundsByResource(events) {
  const byResource = new Map();
  events.forEach((event) => {
    const key = String(event.resourceId);
    if (!byResource.has(key)) byResource.set(key, []);
    byResource.get(key).push(event);
  });

  const flags = new Map();
  byResource.forEach((group) => {
    markTurnarounds(group).forEach((value, id) => flags.set(id, value));
  });
  return flags;
}
